import type { InventoryFailure } from '../../api/inventory/inventoryErrors';

/**
 * What a failed object action means — and whether the run should continue.
 *
 * Shared by the two per-compartment writes on the station page: re-linking a
 * ticked set (`PATCH /product-links/:linkId`, N independent calls) and retiring
 * one (`POST /objects/:assetId/retire`). Both refuse PER OBJECT, so every
 * failure has to answer two questions: does it describe THIS compartment, or
 * the whole attempt? Collapsing the two produces the failure this repo keeps
 * paying for — a service that is down reported as ten compartments each
 * individually refusing, which reads as a data problem and sends the operator
 * looking at bookings instead of at the backend.
 *
 * Between them the two endpoints have exactly four failure shapes, so every one
 * is named rather than passed through:
 *
 *  - **409 `blockers[]`** — a rental is running on this object, or a booking is
 *    committed to it. About this compartment; a run continues past it.
 *  - **404** — the link or the object is gone (retired or re-linked elsewhere
 *    since the page loaded). About this compartment; the list is stale.
 *  - **400** — re-link only: the link's `mode` is not countable (`exclusive`
 *    composites, #1796). About this compartment, and its server text is written
 *    for a developer, so it is not shown.
 *  - **anything else** — 5xx, 401/403, or no response at all. Nothing was
 *    decided about this compartment or any other, so a run stops.
 *
 * The reader classifies; the CALLER chooses the wording, because "the run was
 * stopped" is true of a batch re-link and nonsense for a single retire.
 */
export type ObjectActionAbortReason = 'unreachable' | 'forbidden' | 'service';

export interface ObjectActionFailureRead {
    /** Non-null when the run must stop: the call reached no verdict at all. */
    abort: ObjectActionAbortReason | null;
    /** i18n keys under `station_detail.objects.`, in order, for this position. */
    reasonKeys: string[];
    /**
     * Server text — only for a status we cannot name, so an unforeseen shape
     * still says something specific instead of a generic retry line (#2255).
     */
    detail?: string;
}

export const readObjectActionFailure = (failure: InventoryFailure): ObjectActionFailureRead => {
    // No response at all: `axiosBaseQuery` leaves `status` undefined. The base
    // URL is wrong or the service is down — every remaining call would fail the
    // same way, and its axios text ("Network Error") is not operator language.
    if (failure.unreachable) return { abort: 'unreachable', reasonKeys: [] };

    const status = failure.status ?? 0;
    if (status === 401 || status === 403) return { abort: 'forbidden', reasonKeys: [] };
    if (status >= 500) return { abort: 'service', reasonKeys: [] };

    if (failure.blockers.length > 0) {
        return {
            abort: null,
            reasonKeys: failure.blockers.map((blocker) => `blocker_${blocker}`),
        };
    }
    if (status === 404) return { abort: null, reasonKeys: ['change_product_stale'] };
    if (status === 400) return { abort: null, reasonKeys: ['change_product_unsupported_mode'] };

    return { abort: null, reasonKeys: [], detail: failure.message };
};
